import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Text,
  Anchor,
  Group,
  Badge,
  MultiSelect,
  Button,
  Flex,
} from "@mantine/core";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { _localization } from "../../../config/location";
import { CategoryModelTreeNew } from "../../../model/TblCategoryModel";
import { TblAttributeModel } from "../../../model/TblAttributeModelFix";

interface ProductMissingAttrTableProps {
  productList: any[];
  totalProducts: number;
  loadingProducts: boolean;
  selectedCategory: CategoryModelTreeNew | null;
  selectedAttribute: TblAttributeModel | null;
  apiAvailableValues: any[];
  productSelections: { [productId: number]: number[] };
  savingProductId: number | null;
  pageIndex: number;
  pageSize: number;
  setPageIndex: (page: number) => void;
  setPageSize: (size: number) => void;
  onSaveProductAttributes: (product: any, selectedValIds: number[]) => void;
}

export const ProductMissingAttrTable: React.FC<ProductMissingAttrTableProps> = ({
  productList,
  totalProducts,
  loadingProducts,
  selectedCategory,
  selectedAttribute,
  apiAvailableValues,
  productSelections,
  savingProductId,
  pageIndex,
  pageSize,
  setPageIndex,
  setPageSize,
  onSaveProductAttributes,
}) => {
  // Local state to keep transient selections before clicking "Áp dụng"
  const [localSelections, setLocalSelections] = useState<{ [productId: number]: number[] }>({});

  // Reset localSelections only when product list or selected attribute changes (prevents wiping unsaved drafts on other rows when one row is applied)
  useEffect(() => {
    setLocalSelections(productSelections || {});
  }, [productList, selectedAttribute]);

  const handleLocalSelectChange = (pId: number, selectedValIds: number[]) => {
    setLocalSelections((prev) => ({
      ...prev,
      [pId]: selectedValIds,
    }));
  };

  // Combine available values from API (res.data.availableValues) or from selectedAttribute
  const displayValueOptions = useMemo(() => {
    if (apiAvailableValues && apiAvailableValues.length > 0) {
      return apiAvailableValues;
    }
    if (
      selectedAttribute?.tblAttributeValueModels &&
      selectedAttribute.tblAttributeValueModels.length > 0
    ) {
      return selectedAttribute.tblAttributeValueModels;
    }
    return [];
  }, [selectedAttribute, apiAvailableValues]);

  // Transform available values into Mantine MultiSelect data options format
  const selectOptions = useMemo(() => {
    return displayValueOptions.map((valObj: any, idx: number) => ({
      value: String(valObj.id || valObj.attributeValueId),
      label: `${idx + 1}. ${valObj.value}`,
    }));
  }, [displayValueOptions]);

  const productColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        enableSorting: false,
        size: 70,
        Cell: ({ row }: any) => (
          <Text size="sm" fw={500} ta="center">
            {(pageIndex - 1) * pageSize + row.index + 1}
          </Text>
        ),
      },
      {
        accessorKey: "productName",
        header: `Tên sản phẩm (Tổng số: ${totalProducts})`,
        size: 320,
        Cell: ({ row }: any) => {
          const product = row.original;
          const pId = product.id || product.itemId;
          const productName =
            product.itemName || product.title || product.name || "Sản phẩm";
          const brandName =
            product.brandName || product.brand || product.hieu || "";

          return (
            <Box>
              <Anchor
                href={`/product-manager/edit-product/${pId}`}
                target="_blank"
                fw={600}
                size="sm"
                c="blue"
                style={{ display: "block", marginBottom: 2 }}
              >
                {productName}
              </Anchor>

              {brandName && (
                <Text size="xs" c="dimmed">
                  <b>Hãng :</b> <Badge size="xs" color="blue">{brandName}</Badge>
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "productCode",
        header: "Mã sản phẩm",
        size: 180,
        Cell: ({ row }: any) => {
          const product = row.original;
          const productCode =
            product.itemCode || product.sku || product.code || "";
          return (
            <Text size="sm" fw={500} c="dark">
              {productCode || "-"}
            </Text>
          );
        },
      },
      {
        accessorKey: "attributeValues",
        header: "Chọn giá trị thuộc tính",
        size: 450,
        Cell: ({ row }: any) => {
          const product = row.original;
          const pId = product.id || product.itemId;
          const isSaving = savingProductId === pId;

          const currentValIds =
            localSelections[pId] !== undefined
              ? localSelections[pId]
              : productSelections[pId] || [];
          const selectedStrValues = currentValIds.map(String);

          if (!selectedAttribute) {
            return (
              <Text c="red" size="sm">
                Cần chọn 1 thuộc tính ở trên để xem
              </Text>
            );
          }

          return (
            <Flex align="center" gap="xs" style={{ width: "100%" }}>
              <Box style={{ flex: 1 }}>
                <MultiSelect
                  placeholder={`-- Chọn giá trị (${selectedAttribute.attributeName}) --`}
                  data={selectOptions}
                  value={selectedStrValues}
                  searchable
                  clearable
                  disabled={isSaving}
                  size="sm"
                  styles={{
                    pill: {
                      backgroundColor: "#d0ebff",
                      border: "1px solid #74c0fc",
                      fontWeight: 600,
                      color: "#1864ab",
                      fontSize: "13px",
                    },
                    input: {
                      borderColor: "#adb5bd",
                      fontSize: "14px",
                    },
                  }}
                  onChange={(newValues: string[]) => {
                    handleLocalSelectChange(pId, newValues.map(Number));
                  }}
                />
              </Box>

              <Button
                size="sm"
                color="blue"
                variant="filled"
                loading={isSaving}
                style={{ fontSize: "14px", fontWeight: 600, flexShrink: 0 }}
                onClick={() => {
                  onSaveProductAttributes(product, currentValIds);
                }}
              >
                Áp dụng
              </Button>
            </Flex>
          );
        },
      },
    ],
    [
      pageIndex,
      pageSize,
      totalProducts,
      selectedAttribute,
      selectOptions,
      productSelections,
      localSelections,
      savingProductId,
      onSaveProductAttributes,
    ]
  );

  const productTable = useMantineReactTable({
    columns: productColumns,
    data: productList || [],
    enableTopToolbar: false,
    enableBottomToolbar: true,
    manualPagination: true,
    rowCount: totalProducts,
    onPaginationChange: (updater: any) => {
      const nextState =
        typeof updater === "function"
          ? updater({ pageIndex: pageIndex - 1, pageSize })
          : updater;
      setPageIndex(nextState.pageIndex + 1);
      setPageSize(nextState.pageSize);
    },
    localization: _localization,
    layoutMode: "semantic",
    mantineTableProps: {
      style: {
        width: "100%",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    enableColumnPinning: true,
    initialState: {
      density: "xs",
      columnPinning: {
        right: ["attributeValues"],
      },
    },
    state: {
      isLoading: loadingProducts,
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize: pageSize,
      },
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "sm",
      rowsPerPageOptions: ["10", "20", "50", "100"],
    },
    paginationDisplayMode: "pages",
  });

  return (
    <Box style={{ position: "relative", overflowX: "auto" }}>
      <MantineReactTable table={productTable} />
    </Box>
  );
};
